import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useApiUrls from './useApiUrls';

const LoginPage = () => {
  const [userName, setUserName] = useState('');
  const [passWord, setPassWord] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { getUrlByOperation, error: urlError } = useApiUrls();

  // Load saved credentials on component mount
  useEffect(() => {
    const savedUserName = localStorage.getItem('savedUserName');
    const savedPassWord = localStorage.getItem('savedPassWord');
    const wasRemembered = localStorage.getItem('rememberMe') === 'true';

    if (wasRemembered && savedUserName && savedPassWord) {
      setUserName(savedUserName);
      setPassWord(savedPassWord);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    const loginUrl = getUrlByOperation("login");
    
    let item = { userName, passWord };
    
    try {
      let result = await fetch(loginUrl, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item),
        credentials: 'include' // Important: This ensures cookies are sent/received
      });
      
      result = await result.json();
      
      if (result.responsestatus === "SUCCESS") {
        const { token, userid } = result;
        
        // Handle "Remember Me" functionality
        if (rememberMe) {
          localStorage.setItem('savedUserName', userName);
          localStorage.setItem('savedPassWord', passWord);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('savedUserName');
          localStorage.removeItem('savedPassWord');
          localStorage.removeItem('rememberMe');
        }
        
        // Set token and userid in cookies with a 24-hour expiration
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + 24 * 60 * 60 * 1000);
        
        // Set cookies with more flexible options for cross-environment compatibility
        document.cookie = `token=${token}; path=/; expires=${expirationDate.toUTCString()}; SameSite=Lax`;
        document.cookie = `userid=${userid}; path=/; expires=${expirationDate.toUTCString()}; SameSite=Lax`;
        
        // Also store in localStorage as a fallback
        localStorage.setItem('token', token);
        localStorage.setItem('userid', userid);
        localStorage.setItem('welcomeMessageShown', 'false');
        
        // Add a small delay before navigation to ensure cookie setting completes
        setTimeout(() => {
          navigate("/");
        }, 100);
      } else {
        setErrorMessage("Login failed. Please check your username and password.");
      }
    } catch (error) {
      console.error("Error during login:", error);
      setErrorMessage("An error occurred during login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f7f7f7',
    padding: '0 20px',
  };

  const loginBoxStyle = {
    width: '100%',
    maxWidth: '400px',
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.1)',
  };

  const logoStyle = {
    textAlign: 'center',
    marginBottom: '20px',
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
  };

  const cardBodyStyle = {
    marginBottom: '20px',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    marginBottom: '10px',
    borderRadius: '5px',
    border: '1px solid #ccc',
    fontSize: '16px',
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '15px',
    marginTop: '5px',
  };

  const checkboxStyle = {
    width: '18px',
    height: '18px',
    marginRight: '8px',
    cursor: 'pointer',
  };

  const checkboxLabelStyle = {
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const buttonStyle = {
    width: '100%',
    padding: '10px',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '5px',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    opacity: isLoading ? 0.7 : 1,
  };

  const errorStyle = {
    color: 'red',
    textAlign: 'center',
    marginBottom: '10px',
  };

  return (
    <div style={containerStyle}>
      <div style={loginBoxStyle}>
        <div style={logoStyle}>Login</div>
        <form onSubmit={handleLogin}>
          <div style={cardBodyStyle}>
            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}
            <input
              type="text"
              placeholder="Username"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              style={inputStyle}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={passWord}
              onChange={(e) => setPassWord(e.target.value)}
              style={inputStyle}
              required
            />
            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={checkboxStyle}
              />
              <label 
                htmlFor="rememberMe" 
                style={checkboxLabelStyle}
              >
                Remember Me
              </label>
            </div>
          </div>
          <button 
            type="submit" 
            style={buttonStyle} 
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;